import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { ethers } from 'ethers';
import { useNetwork } from './NetworkContext';

interface WalletContextType {
  provider: ethers.BrowserProvider | null;
  signer: ethers.Signer | null;
  account: string;
  isConnected: boolean;
  connectWallet: () => Promise<void>;
  disconnectWallet: () => void;
  error: string;
  loading: boolean;
  onAccountChange: (callback: (newAccount: string) => void) => () => void;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

interface WalletProviderProps {
  children: ReactNode;
}

export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const { isNetworkSupported } = useNetwork();
  const [provider, setProvider] = useState<ethers.BrowserProvider | null>(null);
  const [signer, setSigner] = useState<ethers.Signer | null>(null);
  const [account, setAccount] = useState<string>('');
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(true); // Start with loading true for auto-reconnect
  const [accountChangeCallbacks, setAccountChangeCallbacks] = useState<Set<(newAccount: string) => void>>(new Set());

  const connectWallet = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      if (typeof window.ethereum !== 'undefined') {
        const provider = new ethers.BrowserProvider(window.ethereum);
        const accounts = await provider.send('eth_requestAccounts', []);
        const signer = await provider.getSigner();
        
        // Check if current network is supported
        const chainId = await provider.getNetwork().then(n => n.chainId);
        
        setProvider(provider);
        setSigner(signer);
        setAccount(accounts[0]);
        setIsConnected(true);
        
        // Save connection state to localStorage
        localStorage.setItem('walletConnected', 'true');
        
        if (!isNetworkSupported(Number(chainId))) {
          setError('Current network is not supported. Please switch to a supported network.');
          return;
        }
      } else {
        setError('MetaMask is not installed. Please install MetaMask to use this app.');
      }
    } catch (err) {
      setError('Failed to connect wallet: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [isNetworkSupported]);

  const disconnectWallet = useCallback(() => {
    setProvider(null);
    setSigner(null);
    setAccount('');
    setIsConnected(false);
    setError('');
    // Clear the connection state from localStorage
    localStorage.removeItem('walletConnected');
  }, []);

  const onAccountChange = useCallback((callback: (newAccount: string) => void) => {
    setAccountChangeCallbacks(prev => new Set(prev).add(callback));
    
    // Return unsubscribe function
    return () => {
      setAccountChangeCallbacks(prev => {
        const newSet = new Set(prev);
        newSet.delete(callback);
        return newSet;
      });
    };
  }, []);

  // Auto-reconnect on page load
  const autoReconnect = useCallback(async () => {
    try {
      if (typeof window.ethereum !== 'undefined') {
        // Check if user was previously connected
        const wasConnected = localStorage.getItem('walletConnected') === 'true';
        
        if (wasConnected) {
          // Try to reconnect without requesting accounts (silent connection)
          const provider = new ethers.BrowserProvider(window.ethereum);
          const accounts = await provider.send('eth_accounts', []); // Use eth_accounts instead of eth_requestAccounts
          
          if (accounts.length > 0) {
            const signer = await provider.getSigner();
            const chainId = await provider.getNetwork().then(n => n.chainId);
            
            // Check network support before setting connection state
            if (!isNetworkSupported(Number(chainId))) {
              setError('Current network is not supported. Please switch to a supported network.');
              // Don't set connection state if network is not supported
            } else {
              // Only set connection state if network is supported
              setProvider(provider);
              setSigner(signer);
              setAccount(accounts[0]);
              setIsConnected(true);
              setError(''); // Clear any previous errors
            }
          } else {
            // No accounts available, clear the connection state
            localStorage.removeItem('walletConnected');
          }
        }
      }
    } catch (err) {
      // Silent fail for auto-reconnect
      console.log('Auto-reconnect failed:', err);
      localStorage.removeItem('walletConnected');
    } finally {
      setLoading(false);
    }
  }, [isNetworkSupported]);

  // Auto-reconnect on component mount
  useEffect(() => {
    autoReconnect();
  }, [autoReconnect]);

  // Listen for account changes
  useEffect(() => {
    if (window.ethereum) {
      const handleAccountsChanged = (accounts: string[]) => {
        if (accounts.length === 0) {
          disconnectWallet();
        } else {
          const newAccount = accounts[0];
          // Update account without full reconnection
          setAccount(newAccount);
          if (provider) {
            provider.getSigner().then(signer => {
              setSigner(signer);
            });
          }
          
          // Notify all registered callbacks about the account change
          accountChangeCallbacks.forEach(callback => {
            try {
              callback(newAccount);
            } catch (error) {
              console.error('Error in account change callback:', error);
            }
          });
        }
      };

      const handleChainChanged = async () => {
        // Clear any previous network error when chain changes
        setError('');
        
        // Check if the new network is supported
        if (provider) {
          try {
            const chainId = await provider.getNetwork().then(n => n.chainId);
            if (!isNetworkSupported(Number(chainId))) {
              setError('Current network is not supported. Please switch to a supported network.');
            }
          } catch (err) {
            console.log('Error checking network after chain change:', err);
          }
        }
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);

      return () => {
        if (window.ethereum) {
          window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
          window.ethereum.removeListener('chainChanged', handleChainChanged);
        }
      };
    }
  }, [disconnectWallet, provider, isNetworkSupported, accountChangeCallbacks]);

  const value: WalletContextType = {
    provider,
    signer,
    account,
    isConnected,
    connectWallet,
    disconnectWallet,
    error,
    loading,
    onAccountChange,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};
